import { Injectable } from '@angular/core';

// Environment
import { environment } from '@environments/environment';

// HandleError
import { handleError } from '@shared-functions/handle-error.function';

// HTTP
import { HttpClient, HttpParams } from '@angular/common/http';

// Interfaces
import { Entidad } from '../interfaces/entidad.interface';
import { EntidadForm } from '../interfaces/entidad-form.interface';
import { EntidadJuzgado, TipoEntidad } from '@entidad/interfaces';
import { GenericResponse } from '@shared-interfaces/generic-response.interface';
import { LogoEntidad } from '@entidad/interfaces/entidad-logo.interface';
import { UnidadFijaEntidad } from '@entidad/interfaces/unidad-fija-entidad.interface';
import { UploadImageResponse } from '../interfaces/upload-image-response.interface';
import { ParametroConfiguracion } from '../interfaces/parametro-configuracion.interface';
// RxJS
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  forkJoin,
  Observable,
  ReplaySubject,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';

// Services
import { WebSocketService } from '@core-services/websocket.service';

@Injectable({
  providedIn: 'root',
})
export class EntidadService {
  private API_SERVER = environment.API_SERVER;

  // 1. CAMBIO PRINCIPAL: Usamos BehaviorSubject inicializado en null
  // Esto permite acceder a .value en cualquier momento.
  private entidadSubject = new BehaviorSubject<Entidad | null>(null);
  private entidadLogosSubject = new BehaviorSubject<LogoEntidad[] | []>([]);

  private unidadFijaEntidad = new BehaviorSubject<UnidadFijaEntidad | null>(null);
  private logoSistemaSubject = new BehaviorSubject<string>('');

  // Observables públicos
  entidad$ = this.entidadSubject.asObservable();
  logos$ = this.entidadLogosSubject.asObservable();
  unidadFijaEntidad$ = this.unidadFijaEntidad.asObservable();
  logoSistema$ = this.logoSistemaSubject.asObservable();

  constructor(private http: HttpClient) {}

  get entidadValue(): Entidad | null {
    return this.entidadSubject.value;
  }

  // GETS
  getEntidad(): Observable<Entidad> {
    return this.http.get<Entidad>(`${this.API_SERVER}/entity/user`).pipe(catchError(handleError));
  }
  getUnidadFijaEntidad(): Observable<UnidadFijaEntidad> {
    return this.http
      .get<UnidadFijaEntidad>(`${this.API_SERVER}/entity/uf`)
      .pipe(catchError(handleError));
  }

  getParametrosEntidad(entidadId: string): Observable<ParametroConfiguracion[]> {
    return this.http
      .get<ParametroConfiguracion[]>(`${this.API_SERVER}/entity/parameters/${entidadId}`)
      .pipe(catchError(handleError));
  }

  saveParametrosEntidad(
    entidadId: string,
    parametros: ParametroConfiguracion[],
  ): Observable<{ success: boolean; message: string }> {
    return this.http
      .put<{ success: boolean; message: string }>(
        `${this.API_SERVER}/entity/parameters/${entidadId}`,
        { parametros },
      )
      .pipe(catchError(handleError));
  }

  getAllEntidades(): Observable<Entidad[]> {
    return this.http.get<Entidad[]>(`${this.API_SERVER}/entity/all`).pipe(catchError(handleError));
  }

  getAllTipoEntidades(): Observable<TipoEntidad[]> {
    return this.http
      .get<TipoEntidad[]>(`${this.API_SERVER}/entity/tipos`)
      .pipe(catchError(handleError));
  }

  getLogo(path: string): Observable<Blob> {
    return this.http
      .get(`${this.API_SERVER}/${path}`, {
        responseType: 'blob',
      })
      .pipe(catchError(handleError));
  }

  getLogosPorEntidad(): Observable<LogoEntidad[]> {
    return this.http
      .get<LogoEntidad[]>(`${this.API_SERVER}/entity/logos/`)
      .pipe(catchError(handleError));
  }
  getEntidades(
    tipoEntidad?: string | null,
    entidadId?: string | null,
  ): Observable<EntidadJuzgado[]> {
    console.log('enra la peticion?:');
    let params = new HttpParams();

    if (tipoEntidad != null) {
      params = params.set('tipoEntidad', tipoEntidad);
    } else {
      params = params.set('tipoEntidad', 'null');
    }

    if (entidadId != null) {
      params = params.set('entidadId', entidadId);
    } else {
      params = params.set('entidadId', 'null');
    }

    return this.http
      .get<EntidadJuzgado[]>(`${this.API_SERVER}/entity/filter?${params.toString()}`)
      .pipe(catchError(handleError));
  }
  refreshDatosGlobales(): void {
    this.loadDatosEntidad();
  }
  actualizarLogoSistema(url: string) {
    this.logoSistemaSubject.next(url);
  }
  // POSTS
  createEntidad(entidadForm: EntidadForm): Observable<GenericResponse> {
    console.log(entidadForm);
    return this.http
      .post<GenericResponse>(`${this.API_SERVER}/entity/create`, entidadForm)
      .pipe(catchError(handleError));
  }

  // PUTS
  updateEntidad(entidadForm: EntidadForm, entidadId: string): Observable<GenericResponse> {
    return this.http
      .put<GenericResponse>(`${this.API_SERVER}/entity/update/${entidadId}`, entidadForm)
      .pipe(catchError(handleError));
  }

  uploadLogo(
    entidadId: number,
    posicion: string,
    codigo: string,
    formData: FormData,
  ): Observable<UploadImageResponse> {
    return this.http
      .put<UploadImageResponse>(
        `${this.API_SERVER}/entity/${entidadId}/logo/${posicion}/codigo/${codigo}`,
        formData,
      )
      .pipe(catchError(handleError));
  }

  loadDatosEntidad(): void {
    console.log('🔄 Cargando datos globales de entidad...');

    forkJoin({
      entidad: this.getEntidad(),
      unidadFija: this.getUnidadFijaEntidad().pipe(catchError(() => of(null))),
    })
      .pipe(
        tap(({ entidad, unidadFija }) => {
          console.log(' Datos básicos cargados:', entidad);

          this.entidadSubject.next(entidad);
          this.unidadFijaEntidad.next(unidadFija);
        }),
        switchMap(() => this.getLogosPorEntidad()),
        tap((logos: LogoEntidad[]) => {
          console.log(' Logos cargados:', logos);
          this.entidadLogosSubject.next(logos);

          const logoSistema = logos.find((l) => l.posicion === 'logo_sistema');
          if (logoSistema) {
            this.actualizarLogoSistema(`${this.API_SERVER}/${logoSistema.path}?t=${Date.now()}`);
          }
        }),
        take(1),
        catchError((err) => {
          console.error('Error cargando datos de entidad', err);
          return EMPTY;
        }),
      )
      .subscribe();
  }
  clearDatosEntidad(): void {
    this.entidadLogosSubject.next([]);
    this.entidadSubject.next(null);
  }
}
